import {observable} from "mobx";
import api from "../Services/Api";
import avitoAccountsSettingsStorage from "./AvitoAccountsSettingsStorage";
import OrderIntegrationSettingsStore from "./OrderIntegrationSettingsStore";
import {OrderIntegrationSettingsDto, StockFiltrationSettingsDto} from "../Entities/Dtos";
import StockFiltrationSettings from "./StockFiltrationSettingsStore";
import StockStore from "../Entities/StockStore";

export default class AvitoAccountSettingsStorage {
    id: string;
    isNew: boolean = false;
    @observable name: string;
    @observable clientId: string;
    @observable clientSecret: string;
    updatingTokenError?: string;
    @observable unloadId?: string;
    @observable hasChanges: boolean = false;
    @observable saving: boolean = false;
    @observable deleting: boolean = false;
    @observable messengerIsEnabled: boolean;
    @observable orderIntegrationIsEnabled: boolean;
    @observable orderIntegrationSettings: OrderIntegrationSettingsStore;
    @observable agreementIsAccepted: boolean = false;
    @observable stockTransferIsEnabled: boolean;
    @observable stockTransferFromCustomFieldIsEnabled: boolean;
    @observable customStockFieldId?: string;
    @observable priceTransferIsEnabled: boolean;
    @observable adUrlFieldId?: string;
    @observable adStatusFieldId?: string;
    @observable priceForTransferId?: string;
    @observable avitoIdFieldId?: string;
    @observable stockFiltrationSettings: StockFiltrationSettings;
    
    constructor(name: string, id?: string, clientId?: string, 
                clientSecret?: string, updatingTokenError?: string, unloadId?: string, 
                messengerIsEnabled?: boolean, orderIntegrationIsEnabled?: boolean, 
                orderIntegrationSettings?: OrderIntegrationSettingsDto, 
                stockFiltrationSettings?: StockFiltrationSettingsDto,
                stockTransferIsEnabled?: boolean,
                customStockFieldId?: string,
                priceForTransferId?: string,
                avitoIdFieldId?: string,
                adUrlFieldId?: string,
                adStatusFieldId?: string) {
        this.isNew = !id;
        this.id = id ?? 'new';
        this.name = name;
        this.clientId = clientId ?? '';
        this.clientSecret = clientSecret ?? '';
        this.updatingTokenError = updatingTokenError;
        this.unloadId = unloadId;
        this.messengerIsEnabled = messengerIsEnabled ?? false;
        this.orderIntegrationIsEnabled = orderIntegrationIsEnabled ?? false;
        this.orderIntegrationSettings = orderIntegrationSettings !== undefined ? 
            new OrderIntegrationSettingsStore(
                ()=> {
                    this.hasChanges = true;
                },
                false,
                orderIntegrationSettings.organization,
                orderIntegrationSettings.salesChannel,
                orderIntegrationSettings.department,
                orderIntegrationSettings.group,
                orderIntegrationSettings.companyType,
                orderIntegrationSettings.counterparty,
                orderIntegrationSettings.numberField,
                orderIntegrationSettings.trackNumberField,
                orderIntegrationSettings.dispatchNumberField,
                orderIntegrationSettings.deliveryServiceNameField,
                orderIntegrationSettings.deliveryAddressField,
                orderIntegrationSettings.buyerFullNameField,
                orderIntegrationSettings.buyerPhoneNumberField,
                orderIntegrationSettings.statesMapping,
                orderIntegrationSettings.componentPriceForUnpackBundle,
                orderIntegrationSettings.reservePositions,
                orderIntegrationSettings.removeCommissionFromPrice,
                orderIntegrationSettings.removeDiscountFromPrice,
                orderIntegrationSettings.vat,
                orderIntegrationSettings.vatMode) :
            new OrderIntegrationSettingsStore(()=> { this.hasChanges = true; }, true);
        this.stockFiltrationSettings = stockFiltrationSettings !== undefined ?
            new StockFiltrationSettings(()=> { this.hasChanges = true; }, 
                stockFiltrationSettings.filtrationMethod, 
                stockFiltrationSettings.stores.map(s=> new StockStore(s.id, s.isActive, ()=> { this.hasChanges = true; })))
            : new StockFiltrationSettings(()=> { this.hasChanges = true; });
        this.stockTransferIsEnabled = stockTransferIsEnabled ?? false;
        this.customStockFieldId = customStockFieldId;
        this.stockTransferFromCustomFieldIsEnabled = customStockFieldId !== undefined;
        this.priceForTransferId = priceForTransferId;
        this.priceTransferIsEnabled = priceForTransferId !== undefined;
        this.adUrlFieldId = adUrlFieldId;
        this.adStatusFieldId = adStatusFieldId;
        this.avitoIdFieldId = avitoIdFieldId;
    }

    async save() {
        if (!this.canSave || this.saving) {
            return;
        }

        this.changeSaving(true);
        try {
            const result = await api.post(this.getSaveUrl, {
                id: this.id,
                unloadId: this.unloadId ? this.unloadId : null,
                name: this.name,
                clientId: this.clientId,
                clientSecret: this.clientSecret,
                messengerIsEnabled: this.messengerIsEnabled,
                orderIntegrationIsEnabled: this.orderIntegrationIsEnabled,
                orderIntegrationSettings: this.orderIntegrationSettings.serialize(),
                stockTransferIsEnabled: this.stockTransferIsEnabled,
                adUrlFieldId: this.adUrlFieldId ? this.adUrlFieldId : null,
                adStatusFieldId: this.adStatusFieldId ? this.adStatusFieldId : null,
                priceForTransferId: this.priceForTransferId ? this.priceForTransferId : null,
                avitoIdFieldId: this.avitoIdFieldId ? this.avitoIdFieldId : null,
                customStockFieldId: this.customStockFieldId ? this.customStockFieldId : null,
                stockFiltrationSettings: this.stockFiltrationSettings?.serialize()
            });
            if (result.success) {
                await avitoAccountsSettingsStorage.load();
            }
        } finally {
            this.changeSaving(false);
        }
    }

    private get getSaveUrl(): string {
        if (this.isNew) {
            return 'avitoAccounts/create';
        }

        return 'avitoAccounts/edit';
    }

    async delete() {
        this.deleting = true;
        try {
            const result = await api.post("avitoAccounts/delete", {
                id: this.id
            })

            if (result.success) {
                await avitoAccountsSettingsStorage.load();
            }
        } finally {
            this.deleting = false;
        }
    }

    get canSave() {
        if (this.priceTransferIsEnabled && !this.priceForTransferId) {
            return false;
        }
        
        if (this.stockTransferFromCustomFieldIsEnabled && !this.customStockFieldId) {
            return false;
        }
        
        const orderSettingsCanSave = !this.orderIntegrationIsEnabled || this.orderIntegrationSettings.canSave;
        const stockFiltrationSettingsCanSave = this.stockFiltrationSettings === undefined ? true : this.stockFiltrationSettings.canSave;
        return this.hasChanges && 
            this.name && this.clientId && 
            this.clientSecret && 
            (!this.isNew || this.agreementIsAccepted) && 
            orderSettingsCanSave && stockFiltrationSettingsCanSave;
    }

    changeUnload(unloadId?: string) {
        this.unloadId = unloadId;
        this.hasChanges = true;
    }

    changeMessengerEnabled(value: boolean) {
        this.messengerIsEnabled = value;
        this.hasChanges = true;
    }

    changeStockTransferEnabled(value: boolean) {
        this.stockTransferIsEnabled = value;
        this.hasChanges = true;
        if (value) {
            this.customStockFieldId = undefined;
            this.stockTransferFromCustomFieldIsEnabled = false;
        }
    }

    changePriceTransferEnabled(value: boolean) {
        if (!value) {
            this.priceForTransferId = undefined;
        }
        
        this.priceTransferIsEnabled = value;
        this.hasChanges = true;
    }

    changeClientId(value: string) {
        if (!this.isNew) {
            return;
        }
        this.clientId = value;
        this.hasChanges = true;
    }

    changeClientSecret(value: string) {
        if (!this.isNew) {
            return;
        }
        this.clientSecret = value;
        this.hasChanges = true;
    }
    
    changeAgreementAccepted(value: boolean) {
        this.agreementIsAccepted = value;
    }

    private changeSaving(value: boolean) {
        this.saving = value;
    }

    changeOrderIntegrationEnabled(value: boolean) {
        this.orderIntegrationIsEnabled = value;
        this.hasChanges = true;
    }
    
    changeAdUrlFieldId(value?: string) {
        this.adUrlFieldId = value;
        this.hasChanges = true;
    }

    changeAdStatusFieldId(value?: string) {
        this.adStatusFieldId = value;
        this.hasChanges = true;
    }

    changePriceForTransferId(value?: string) {
        this.priceForTransferId = value;
        this.hasChanges = true;
    }
    
    changeAvitoIdFieldId(value?: string) {
        this.avitoIdFieldId = value;
        this.hasChanges = true;
    }
    
    changeCustomStockFieldId(value?: string) {
        this.customStockFieldId = value;
        this.hasChanges = true;
    }
    
    changeStockTransferFromCustomFieldEnabled(value: boolean) {
        this.stockTransferFromCustomFieldIsEnabled = value;
        this.hasChanges = true;
        if (value) {
            this.stockTransferIsEnabled = false;
        }
    }
}
